import {
	SampleMatrix,
	SampleSubMatrix,
	Severity,
	SPECIFICATION_ALLOWED_MIME_TYPES,
	SPECIFICATION_FILE_SIZE_LIMIT,
} from "node-library/dist/constants";
import { FrontendSampleSpecification } from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import {
	SetStateAction,
	useEffect,
	useState,
	ChangeEvent,
	DragEvent,
} from "react";
import {
	generateUniqueFileName,
	getFilenameAndExtension,
} from "node-library/dist/helpers/file_helper";
import { LinearProgress, Paper } from "@mui/material";
import { CustomDropdown } from "../../../common-components/inputs";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	getMatrixTranslationText,
	getSubMatrixKeysFromMatrix,
	getSubMatrixTranslationText,
} from "../../../helpers/sampleHelper";
import { ModalWithButtons } from "../../../common-components/modals/modal";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import { BlueButton } from "../../../common-components/button";
import { SemanticColor } from "node-library/dist/colors";
import { AlertIcon } from "../../../common-components/svgIcons";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;

	matrix: SampleMatrix;
	subMatrix: SampleSubMatrix;

	selectedSpecification: FrontendSampleSpecification;

	userSpecifications: Array<FrontendSampleSpecification>;

	setUserSpecifications: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleSpecification>>
	>;
}

export default function MoveSpecificationModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { open, setOpen, setUserSpecifications } = props;

	const [matrix, setMatrix] = useState<SampleMatrix>(props.matrix);
	const [subMatrix, setSubMatrix] = useState<SampleSubMatrix>(props.subMatrix);

	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (open) {
			setMatrix(props.matrix);
			setSubMatrix(props.subMatrix);
		}
	}, [open]);

	function handleMatrixChange(matrix: SampleMatrix) {
		setMatrix(matrix);
		const newSubMatrix: SampleSubMatrix = getSubMatrixKeysFromMatrix(matrix)[0];
		setSubMatrix(newSubMatrix);
	}

	const currentFolderSpecs = props.userSpecifications.filter((spec) => {
		return spec.matrix === matrix && spec.subMatrix === subMatrix;
	});

	const uniqueName = generateUniqueFileName(
		props.selectedSpecification.fileName,
		props.selectedSpecification.format,
		currentFolderSpecs.map((spec) => {
			return { fileName: spec.fileName, extension: spec.format };
		})
	);
	const hasDuplicateName: boolean =
		uniqueName !==
		`${props.selectedSpecification.fileName}.${props.selectedSpecification.format}`;

	async function moveSpecificationToFolder(
		specificationId: string,
		matrix: SampleMatrix,
		subMatrix: SampleSubMatrix
	) {
		if (hasDuplicateName) {
			return;
		}
		setLoading(true);
		try {
			const response = await sendPOSTRequest(
				"/specifications/move-specification",
				{
					specificationId,
					matrix,
					subMatrix,
				}
			);

			props.setUserSpecifications((prevSpecs) =>
				prevSpecs.map((prevSpec, idx) =>
					prevSpec.specificationId === specificationId
						? { ...prevSpec, matrix, subMatrix }
						: prevSpec
				)
			);

			openSnackbar(
				translation("FileMovedSuccess"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			props.setOpen(false);
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	const uploader: React.ReactNode = (
		<div>
			<div className="upload-specification-folder-row">
				<CustomDropdown
					id="matrix-folder-dropdown"
					label={translation("MatrixFolder")}
					options={Object.values(SampleMatrix).map((value) => {
						return {
							value: value,
							title: getMatrixTranslationText(value, translation),
						};
					})}
					value={matrix}
					setValue={handleMatrixChange}
				></CustomDropdown>
				<CustomDropdown
					id="sub-matrix-dropdown"
					label={translation("SubMatrix")}
					options={getSubMatrixKeysFromMatrix(matrix).map((value) => {
						return {
							value: value,
							title: getSubMatrixTranslationText(value, translation),
						};
					})}
					value={subMatrix}
					setValue={(value) => {
						setSubMatrix(value);
					}}
				></CustomDropdown>
			</div>

			{hasDuplicateName &&
				!(matrix == props.matrix && subMatrix == props.subMatrix) && (
					<div
						style={{
							color: SemanticColor.ERROR,
							display: "flex",
							alignItems: "center",
							gap: "5px",
							margin: "5px 0px",
						}}
						className="typography-caption"
					>
						<AlertIcon />
						{translation("DuplicateFilenameError")}
					</div>
				)}
		</div>
	);

	const moveSpecificationModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("Movefile")}
			headerDescription={translation("MovefileDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						margin: "0px 20px",
					}}
				>
					<BlueButton
						key="update-order-status-time-button"
						id="update-order-status-time-button"
						size="flex"
						children={
							loading ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("Move")
							)
						}
						disabled={
							hasDuplicateName ||
							loading ||
							(matrix == props.matrix && subMatrix == props.subMatrix)
						}
						onClick={() => {
							moveSpecificationToFolder(
								props.selectedSpecification.specificationId,
								matrix,
								subMatrix
							);
						}}
					></BlueButton>
				</div>,
			]}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: "10px",
					margin: "0px 20px",
				}}
			>
				{uploader}
			</div>
		</ModalWithButtons>
	);
	return moveSpecificationModal;
}
